import * as React from "react";
import { StatusPill, type StatusVariant } from "../internal/status-pill";

export type MultiValue = string[] | null;

export interface MultiOptions {
  options?: string[];
  variantMap?: Record<string, StatusVariant>;
  /** Override the auto-derived max-visible-chips before "+N more". */
  maxItems?: number;
}

export function MultiChips({ value, options }: { value: string[]; options: MultiOptions }) {
  const max = options.maxItems ?? 3;
  const [expanded, setExpanded] = React.useState(false);
  const visible = expanded ? value : value.slice(0, max);
  const hidden = value.length - max;
  const variantMap = options.variantMap;

  return (
    <div className="inline-flex flex-wrap items-center gap-y-0.5">
      {visible.map((item) => {
        const variant: StatusVariant = variantMap?.[item] ?? "neutral";
        return (
          <StatusPill key={item} variant={variant} className="mr-1 mb-0.5">
            {item}
          </StatusPill>
        );
      })}
      {!expanded && hidden > 0 && (
        <button
          type="button"
          onClick={() => setExpanded(true)}
          className="text-xs text-muted-foreground hover:underline"
        >
          +{hidden} more
        </button>
      )}
    </div>
  );
}

export function MultiEditor({
  value,
  options,
  onCommit,
  onCancel,
}: {
  value: MultiValue;
  options: string[];
  onCommit: (next: MultiValue) => void;
  onCancel: () => void;
}) {
  const [selected, setSelected] = React.useState<Set<string>>(() => new Set(value ?? []));
  const committedRef = React.useRef(false);

  const commit = React.useCallback(() => {
    if (committedRef.current) return;
    committedRef.current = true;
    // Preserve order from the options array.
    const next = options.filter((o) => selected.has(o));
    onCommit(next);
  }, [onCommit, options, selected]);

  const cancel = React.useCallback(() => {
    if (committedRef.current) return;
    committedRef.current = true;
    onCancel();
  }, [onCancel]);

  const toggle = (opt: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(opt)) next.delete(opt);
      else next.add(opt);
      return next;
    });
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      commit();
    } else if (e.key === "Escape") {
      e.preventDefault();
      cancel();
    }
  };

  return (
    <div
      data-multi-editor
      className="inline-flex flex-col gap-1 rounded border border-border bg-background p-2"
      onKeyDown={handleKeyDown}
      tabIndex={-1}
    >
      {options.map((opt, i) => (
        <label key={opt} className="inline-flex items-center gap-2 text-xs">
          <input
            type="checkbox"
            autoFocus={i === 0}
            checked={selected.has(opt)}
            onChange={() => toggle(opt)}
            aria-label={opt}
          />
          <span>{opt}</span>
        </label>
      ))}
    </div>
  );
}
